// ============================================
// ControlExpedientes.js — TORRE DE CONTROL de expedientes (Entrega A)
// ============================================
// Vista GLOBAL de direccion sobre TODOS los expedientes activos de la empresa
// (los 3 flujos fijos). Complementa "Mi Trabajo" (bandeja personal del operador);
// esto es la vista de mando: ver todo para tomar decisiones de operacion.
//
// SOLO LECTURA de estado en esta entrega: se monitorea, se abre, se reasigna y
// se ve el historial de cualquier expediente. Mover de paso por ARRASTRE queda
// para la Entrega B (con candados + golden-master). Para mover un expediente de
// paso hoy, direccion entra al expediente (boton de cada tarjeta/fila).
//
// Backend: GET /api/control-expedientes (src/routes/controlExpedientes.js, authBearer).
// TRANSPARENCIA-TOTAL (2026-07-09): este componente ya NO se monta como pantalla
// aparte; su resumen (StatCard) y su Kanban (VistaKanban) se exportan y reusan dentro
// de Expedientes, que ahora es la vista unica y abierta a todos.

import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { authHeaders, storage, STORAGE_KEYS } from './utils/storage';
import { useResponsive, useAnchoViewport } from './hooks/useResponsive';
import FichaExpediente from './components/expedientes/FichaExpediente';
import ModalAvisarResponsable from './components/expedientes/ModalAvisarResponsable';
import {
  FF, C, SEMAFORO, TIPO_VISUAL, formatMoneda, formatFechaCorta, textoDias, MarcaSinAutorizar, lecturaMonto,
} from './utils/estiloExpedientes';
import {
  Radar, LayoutGrid, Table2, RefreshCw, Search, X, Folder, Wallet,
  AlertTriangle, CheckCircle2, Inbox, ArrowUpDown, User, Clock, ChevronRight, Bell,
} from 'lucide-react';
import { API_BASE } from './config/api';


export default function ControlExpedientes({ esCelular: esCelularProp, embebido = false }) {
  const { isMobile } = useResponsive();
  const ancho = useAnchoViewport();
  const esCelular = esCelularProp != null ? esCelularProp : isMobile;
  // La tabla de 8 columnas necesita ancho; abajo del umbral se muestra en tarjetas.
  const compacto = esCelular || ancho < 1000;

  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null); // id de expediente abierto (ficha) o null
  const [avisarExp, setAvisarExp] = useState(null); // expediente para "avisar al responsable"
  const [toast, setToast] = useState(null);

  // Vista recordada entre sesiones.
  const [vista, setVista] = useState(() => storage.getRaw(STORAGE_KEYS.CONTROL_EXP_VISTA) || 'kanban');
  const cambiarVista = (v) => { setVista(v); storage.set(STORAGE_KEYS.CONTROL_EXP_VISTA, v); };

  // Filtros
  const [filtroTipo, setFiltroTipo] = useState('');       // '' = todos | instalacion | reparaciones | ventas
  const [filtroResp, setFiltroResp] = useState('');       // '' | vendedor_id
  const [filtroSem, setFiltroSem] = useState('');         // '' | verde | amarillo | rojo
  const [busqueda, setBusqueda] = useState('');
  // Orden de la tabla
  const [orden, setOrden] = useState({ campo: 'dias', dir: 'desc' });

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/control-expedientes`, { headers: { ...authHeaders() } });
      if (res.status === 401 || res.status === 403) { setError('permiso'); return; }
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || 'fallo');
      setData(json);
    } catch (e) {
      setError('red');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ===== Derivados =====
  const responsables = useMemo(() => {
    const m = new Map();
    (data?.expedientes || []).forEach((e) => {
      if (e.vendedor_id) m.set(String(e.vendedor_id), e.responsable_nombre || 'Sin nombre');
    });
    return Array.from(m, ([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [data]);

  const pasa = useCallback((exp) => {
    if (filtroTipo && exp.workflow_codigo !== filtroTipo) return false;
    if (filtroResp && String(exp.vendedor_id) !== String(filtroResp)) return false;
    if (filtroSem && exp.semaforo !== filtroSem) return false;
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      const hay = [exp.folio, exp.cliente_nombre, exp.cliente_jerarquia_corta, exp.responsable_nombre, exp.titulo]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }, [filtroTipo, filtroResp, filtroSem, busqueda]);

  const expedientesFiltrados = useMemo(
    () => (data?.expedientes || []).filter(pasa),
    [data, pasa]
  );

  const hayFiltros = filtroTipo || filtroResp || filtroSem || busqueda.trim();
  const limpiarFiltros = () => { setFiltroTipo(''); setFiltroResp(''); setFiltroSem(''); setBusqueda(''); };

  // Columnas del kanban: por flujo (pasos del workflow) o genericas (vista "todos").
  const columnas = useMemo(() => {
    if (!data) return [];
    if (filtroTipo) {
      const wf = (data.workflows || []).find((w) => w.codigo === filtroTipo);
      return wf ? wf.pasos.map((p) => ({ key: p.estado_codigo, label: p.nombre })) : [];
    }
    return (data.etapasGenericas || []).map((e) => ({ key: e.id, label: e.label }));
  }, [data, filtroTipo]);

  const claveColumna = useCallback(
    (exp) => (filtroTipo ? exp.estado : exp.etapa_generica),
    [filtroTipo]
  );

  // ===== Ficha de un expediente (abrir cualquiera, aunque no sea suyo) =====
  if (sel != null) {
    return (
      <FichaExpediente
        id={sel}
        esCelular={esCelular}
        onVolver={() => { setSel(null); cargar(); }}
        onCambiarId={(id) => setSel(id)}
      />
    );
  }

  return (
    <div style={{ fontFamily: FF, background: embebido ? 'transparent' : C.fondo, minHeight: embebido ? 'auto' : '100%', color: C.gris }}>
      <style>{`
        @keyframes beepShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes beepFadeUp { from{opacity:0; transform:translateY(6px)} to{opacity:1; transform:translateY(0)} }
        .beep-skel { background:linear-gradient(90deg,#eceef0 25%,#f6f7f8 50%,#eceef0 75%); background-size:800px 100%; animation:beepShimmer 1.2s infinite linear; border-radius:8px; }
        .beep-card-exp { transition: box-shadow .18s ease-out, transform .18s ease-out, border-color .18s ease-out; -webkit-tap-highlight-color: transparent; }
        .beep-card-exp:hover { box-shadow:0 6px 18px rgba(31,41,55,.10); transform:translateY(-1px); border-color:#cdd4dd; }
        .beep-card-exp:active { transform:scale(.99); }
        .beep-fila-exp { transition: background .15s ease-out; cursor:pointer; }
        .beep-fila-exp:hover { background:#f7f9fb; }
        .beep-chip { transition: all .15s ease-out; -webkit-tap-highlight-color: transparent; cursor:pointer; }
        /* KANBAN-SCROLL: la barra horizontal del carril es VISIBLE a proposito (afordancia:
           es la unica pista de que hay mas columnas). No ocultarla. */
        .beep-kanban-scroll { scrollbar-width: auto; scrollbar-color:#b6bec9 #eef0f3; }
        .beep-kanban-scroll::-webkit-scrollbar { height:12px; width:10px; }
        .beep-kanban-scroll::-webkit-scrollbar-track { background:#eef0f3; border-radius:8px; }
        .beep-kanban-scroll::-webkit-scrollbar-thumb { background:#b6bec9; border-radius:8px; border:2px solid #eef0f3; }
        .beep-kanban-scroll::-webkit-scrollbar-thumb:hover { background:#98a3b2; }
        .beep-kanban-rail { cursor:grab; }
        .beep-kanban-rail:active { cursor:grabbing; }
        /* Scroll vertical de cada columna: discreto, para no competir con el horizontal. */
        .beep-kanban-col { scrollbar-width: thin; scrollbar-color:#d5dae1 transparent; }
        .beep-kanban-col::-webkit-scrollbar { width:8px; }
        .beep-kanban-col::-webkit-scrollbar-thumb { background:#d5dae1; border-radius:8px; }
        .beep-kanban-col::-webkit-scrollbar-track { background:transparent; }
      `}</style>

      {/* ===== Cabecera ===== (embebido oculta el titulo propio; lo pone Expedientes) */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        padding: embebido ? (esCelular ? '2px 0 12px' : '2px 0 14px') : (esCelular ? '14px 16px' : '18px 26px'),
        background: embebido ? 'transparent' : C.blanco, borderBottom: embebido ? 'none' : `1px solid ${C.borde}`,
      }}>
        {embebido ? (
          <div style={{ fontSize: 13, fontWeight: 600, color: C.grisClaro, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Radar size={16} /> Vista global · todos los expedientes en vivo
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, background: '#eef4fb', color: C.navy,
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Radar size={22} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: esCelular ? 17 : 20, fontWeight: 700, color: C.oscuro, lineHeight: 1.15 }}>
                Control de Expedientes
              </div>
              <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 1 }}>
                Vista global de direccion · estado en vivo
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Toggle Kanban / Tabla */}
          <div style={{ display: 'flex', background: C.fondo, borderRadius: 10, padding: 3, border: `1px solid ${C.borde}` }}>
            <BotonToggle activo={vista === 'kanban'} onClick={() => cambiarVista('kanban')} Icono={LayoutGrid} label="Kanban" esCelular={esCelular} />
            <BotonToggle activo={vista === 'tabla'} onClick={() => cambiarVista('tabla')} Icono={Table2} label="Tabla" esCelular={esCelular} />
          </div>
          <button
            onClick={cargar}
            title="Actualizar"
            style={{
              width: 38, height: 38, borderRadius: 10, border: `1px solid ${C.borde}`, background: C.blanco,
              color: C.gris, display: 'grid', placeItems: 'center', cursor: 'pointer',
            }}
          >
            <RefreshCw size={17} style={{ animation: cargando ? 'beepShimmer 1s' : 'none' }} />
          </button>
        </div>
      </div>

      {/* ===== Resumen superior ===== */}
      <ResumenSuperior data={data} cargando={cargando} compacto={compacto} esCelular={esCelular} />

      {/* ===== Filtros ===== */}
      <div style={{ padding: esCelular ? '0 12px 10px' : '0 26px 12px', background: C.blanco, borderBottom: `1px solid ${C.borde}` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          {/* Buscador */}
          <div style={{ position: 'relative', flex: esCelular ? '1 1 100%' : '0 0 260px', minWidth: 180 }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: C.grisClaro }} />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar folio, cliente, responsable…"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 30px 9px 32px', borderRadius: 10,
                border: `1px solid ${C.borde}`, fontFamily: FF, fontSize: 13, outline: 'none', color: C.oscuro,
              }}
            />
            {busqueda && (
              <X size={15} onClick={() => setBusqueda('')} style={{ position: 'absolute', right: 9, top: 10, color: C.grisClaro, cursor: 'pointer' }} />
            )}
          </div>

          {/* Tipo de trabajo */}
          <ChipsFiltro
            valor={filtroTipo}
            onChange={setFiltroTipo}
            opciones={[
              { id: '', label: 'Todos' },
              { id: 'instalacion', label: 'Instalacion' },
              { id: 'reparaciones', label: 'Servicios' },
              { id: 'ventas', label: 'Ventas' },
            ]}
          />

          {/* Semaforo */}
          <ChipsFiltro
            valor={filtroSem}
            onChange={setFiltroSem}
            opciones={[
              { id: '', label: 'Todos' },
              { id: 'rojo', label: 'Atorados', punto: SEMAFORO.rojo.color },
              { id: 'amarillo', label: 'Tardando', punto: SEMAFORO.amarillo.color },
              { id: 'verde', label: 'Al dia', punto: SEMAFORO.verde.color },
            ]}
          />

          {/* Responsable */}
          <select
            value={filtroResp}
            onChange={(e) => setFiltroResp(e.target.value)}
            style={{
              padding: '9px 10px', borderRadius: 10, border: `1px solid ${C.borde}`, fontFamily: FF,
              fontSize: 13, color: C.oscuro, background: C.blanco, cursor: 'pointer', maxWidth: 200,
            }}
          >
            <option value="">Todos los responsables</option>
            {responsables.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>

          {hayFiltros && (
            <button
              onClick={limpiarFiltros}
              className="beep-chip"
              style={{ padding: '8px 12px', borderRadius: 10, border: 'none', background: '#fef2f2', color: '#dc2626', fontFamily: FF, fontSize: 12, fontWeight: 600 }}
            >
              Limpiar filtros
            </button>
          )}
          {!cargando && data && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: C.grisClaro, fontWeight: 600 }}>
              {expedientesFiltrados.length} de {data.expedientes.length}
            </span>
          )}
        </div>
      </div>

      {/* ===== Contenido ===== */}
      <div style={{ padding: embebido ? (esCelular ? '12px 0 0' : '16px 0 0') : (esCelular ? 12 : 20) }}>
        {cargando ? (
          <EstadoCargando vista={vista} esCelular={esCelular} />
        ) : error ? (
          <EstadoError tipo={error} onReintentar={cargar} />
        ) : expedientesFiltrados.length === 0 ? (
          <EstadoVacio hayFiltros={!!hayFiltros} onLimpiar={limpiarFiltros} />
        ) : vista === 'kanban' ? (
          <VistaKanban
            columnas={columnas}
            expedientes={expedientesFiltrados}
            claveColumna={claveColumna}
            onAbrir={setSel}
            onAvisar={setAvisarExp}
            esCelular={esCelular}
            onIrTabla={() => cambiarVista('tabla')}
          />
        ) : (
          <VistaTabla
            expedientes={expedientesFiltrados}
            orden={orden}
            setOrden={setOrden}
            onAbrir={setSel}
            onAvisar={setAvisarExp}
            compacto={compacto}
          />
        )}
      </div>

      {avisarExp && (
        <ModalAvisarResponsable
          exp={avisarExp}
          onClose={() => setAvisarExp(null)}
          onEnviado={(msg) => { setToast(msg || 'Recordatorio registrado.'); setTimeout(() => setToast(null), 3500); }}
        />
      )}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 4100,
          background: C.oscuro, color: '#fff', padding: '11px 18px', borderRadius: 10, fontFamily: FF,
          fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,.25)', maxWidth: '90vw', textAlign: 'center',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ============================================
// Subcomponentes
// ============================================

function BotonToggle({ activo, onClick, Icono, label, esCelular }) {
  return (
    <button
      onClick={onClick}
      className="beep-chip"
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: esCelular ? '7px 10px' : '7px 13px',
        borderRadius: 8, border: 'none', fontFamily: FF, fontSize: 13, fontWeight: 600,
        background: activo ? '#ffffff' : 'transparent',
        color: activo ? '#2f5f9e' : '#8a9099',
        boxShadow: activo ? '0 1px 3px rgba(31,41,55,.12)' : 'none',
      }}
    >
      <Icono size={16} />
      {!esCelular && label}
    </button>
  );
}

function ChipsFiltro({ valor, onChange, opciones }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {opciones.map((o) => {
        const activo = valor === o.id;
        return (
          <button
            key={o.id || 'all'}
            onClick={() => onChange(o.id)}
            className="beep-chip"
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 999,
              border: `1px solid ${activo ? '#2f5f9e' : '#e6e8eb'}`,
              background: activo ? '#2f5f9e' : '#ffffff',
              color: activo ? '#ffffff' : '#4e5357', fontFamily: FF, fontSize: 12.5, fontWeight: 600,
            }}
          >
            {o.punto && <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.punto }} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function StatCard({ Icono, label, valor, sub, color, bg, cargando }) {
  return (
    <div style={{
      background: '#ffffff', borderRadius: 14, border: '1px solid #e6e8eb', padding: '14px 16px',
      display: 'flex', alignItems: 'center', gap: 13, minWidth: 0, flex: '1 1 200px',
    }}>
      <div style={{ width: 42, height: 42, borderRadius: 11, background: bg, color, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icono size={21} />
      </div>
      <div style={{ minWidth: 0 }}>
        {cargando
          ? <div className="beep-skel" style={{ width: 60, height: 22 }} />
          : <div style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', lineHeight: 1.05 }}>{valor}</div>}
        <div style={{ fontSize: 12, color: '#8a9099', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
        {sub && !cargando && <div style={{ fontSize: 11, color: '#a4abb4', marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

function ResumenSuperior({ data, cargando, compacto, esCelular }) {
  const r = data?.resumen;
  return (
    <div style={{
      display: 'flex', gap: 12, flexWrap: 'wrap', padding: esCelular ? '12px' : '16px 26px',
      background: '#ffffff', borderBottom: '1px solid #e6e8eb',
    }}>
      <StatCard Icono={Folder} label="Expedientes activos" cargando={cargando}
        valor={r?.total_activos ?? '—'} color="#2f5f9e" bg="#eef4fb" />
      <StatCard Icono={AlertTriangle} label="Atorados (rojo)" cargando={cargando}
        valor={r?.en_rojo ?? '—'} sub={r ? `${r.en_amarillo} tardando` : null} color="#dc2626" bg="#fef2f2" />
      <StatCard Icono={Wallet} label="Valor en curso" cargando={cargando}
        valor={r ? formatMoneda(r.valor_en_curso, compacto) : '—'}
        // EXPEDIENTES-RESUMEN (2026-09-17): `con_monto` llega NULL a quien no puede ver todos los
        // montos; sin esta guarda se imprimiria "de null con monto". Este ResumenSuperior es hoy
        // CODIGO MUERTO (de este archivo solo se importa VistaKanban), pero se deja sano por si
        // alguien vuelve a montar la pantalla. Borrarlo entero es trabajo de un commit de limpieza.
        sub={r && r.con_monto != null ? `de ${r.con_monto} con monto` : null} color="#5c9adb" bg="#eef4fb" />
      <StatCard Icono={CheckCircle2} label="Cerrados este mes" cargando={cargando}
        valor={r?.cerrados_mes ?? '—'} color="#16a34a" bg="#eefaf1" />
    </div>
  );
}

// ---------- Boton compacto "Avisar al responsable" (solo atrasados) ----------
function BotonAvisar({ exp, onAvisar, full }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onAvisar(exp); }}
      title="Avisar al responsable"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        width: full ? '100%' : 'auto', marginTop: full ? 8 : 0, padding: full ? '7px 10px' : '5px 9px',
        borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626',
        fontFamily: FF, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
      }}
    >
      <Bell size={12} /> Avisar al responsable
    </button>
  );
}

// ---------- Tarjeta de expediente (kanban) ----------
// MOVIL-2 (2026-09-14): en celular la tarjeta es COMPACTA, de dos renglones, para que quepan 5+ por
// pantalla. Antes ocupaba 5 renglones (folio, tipo, cliente, responsable, dias, monto y un boton de
// "Avisar al responsable") y solo entraban dos. El boton de avisar se saca de la cara de la tarjeta:
// vive dentro del detalle del expediente, que es donde se decide.
//   Renglon 1: CLIENTE en negrita + chip de tipo a la derecha.
//   Renglon 2: ultimos 4 del folio + dias en paso con reloj + monto + iniciales del responsable.
// La barra de color de la izquierda (3 px) sigue siendo el semaforo.
// EXPEDIENTES-RESUMEN FRENTE E (2026-09-17): DOS letras como maximo, y solo letras. Un nombre
// guardado con un guion, un punto o un emoji delante colaba ese caracter dentro del circulo y lo
// desbordaba. Se filtra a letras (incluidas las acentuadas) antes de tomar la inicial.
const inicialesDe = (nombre) => String(nombre || '')
  .trim().split(/\s+/).filter(Boolean)
  .map(p => (p.match(/[\p{L}\p{N}]/u) || [''])[0])
  .filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
const ultimos4Folio = (folio, id) => {
  const s = String(folio || '');
  return s ? s.slice(-4) : `#${id}`;
};

function TarjetaExpediente({ exp, onAbrir, onAvisar, idx, compacta }) {
  const sem = SEMAFORO[exp.semaforo] || SEMAFORO.verde;
  const tipo = TIPO_VISUAL[exp.workflow_codigo] || { label: exp.workflow_nombre || '—', color: '#4e5357', bg: '#f3f4f4' };
  const dinero = lecturaMonto(exp, true);

  if (compacta) {
    return (
      <div
        className="beep-card-exp"
        onClick={() => onAbrir(exp.id)}
        style={{
          background: '#ffffff', border: '1px solid #e6e8eb', borderLeft: `3px solid ${sem.color}`,
          borderRadius: 10, padding: '9px 10px', cursor: 'pointer', minHeight: 44,
          animation: `beepFadeUp .25s ease-out ${Math.min(idx, 10) * 25}ms both`,
        }}
      >
        {/* Renglon 1: cliente + tipo.
            FRENTE E (2026-09-17): el nombre del cliente cabe en DOS renglones y se corta por
            palabra completa con puntos suspensivos (line-clamp corta entre palabras, nunca a
            media palabra: "Comercializadora Beep Me…" partido asi no vale). `minWidth: 0` es
            imprescindible — sin el, un hijo de un flex no baja de su ancho de contenido y el
            texto se sale de la caja por mucho ellipsis que se le ponga. El chip de tipo se alinea
            ARRIBA para que no quede flotando a media altura cuando el nombre ocupa dos renglones. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <span
            title={exp.cliente_nombre || 'Sin cliente'}
            style={{
              minWidth: 0, flex: 1,
              fontSize: 13.5, fontWeight: 700, color: '#1f2937', lineHeight: 1.25,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', overflowWrap: 'anywhere',
            }}
          >
            {exp.cliente_nombre || 'Sin cliente'}
          </span>
          <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: tipo.color, background: tipo.bg, padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap' }}>
            {tipo.label}
          </span>
        </div>
        {/* Renglon 2: folio + dias + monto + responsable.
            FRENTE E: "Sin cotizar · 58 dias" no cabe aqui en un telefono de 360 px; `lecturaMonto`
            lo abrevia a "Sin cotizar 58d" en modo compacto y el texto completo queda en el tooltip.
            Ademas el monto puede ENVOLVER a un segundo renglon (`flexWrap`) en vez de desbordarse:
            mas vale una tarjeta un pelin mas alta que texto saliendose de su caja. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#8a9099', fontFamily: 'ui-monospace, monospace', flexShrink: 0 }}>
            {ultimos4Folio(exp.folio, exp.id)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: sem.color, flexShrink: 0 }}>
            <Clock size={10} /> {textoDias(exp.dias_en_paso)}
          </span>
          <span
            title={dinero.titulo}
            style={{
              marginLeft: 'auto', minWidth: 0, fontSize: dinero.estado === 'sin_cotizar' ? 10.5 : 12.5,
              fontWeight: 700, color: dinero.color, whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >{dinero.texto}</span>
          <span
            title={exp.responsable_nombre || 'Sin responsable'}
            aria-label={exp.responsable_nombre || 'Sin responsable'}
            style={{
              flexShrink: 0, width: 24, height: 24, minWidth: 24, borderRadius: '50%', background: '#eef4fb',
              color: '#2f5f9e', fontSize: 10, fontWeight: 800, letterSpacing: -.2,
              // lineHeight:1 + overflow:hidden: el circulo manda sobre el texto, nunca al reves.
              lineHeight: 1, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >{inicialesDe(exp.responsable_nombre)}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="beep-card-exp"
      onClick={() => onAbrir(exp.id)}
      style={{
        background: '#ffffff', border: '1px solid #e6e8eb', borderLeft: `3px solid ${sem.color}`,
        borderRadius: 12, padding: '11px 12px', cursor: 'pointer',
        animation: `beepFadeUp .25s ease-out ${Math.min(idx, 10) * 30}ms both`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#2f5f9e', letterSpacing: .2 }}>{exp.folio || `#${exp.id}`}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: tipo.color, background: tipo.bg, padding: '2px 7px', borderRadius: 6 }}>{tipo.label}</span>
      </div>
      <div
        title={exp.cliente_nombre || 'Sin cliente'}
        style={{ fontSize: 13.5, fontWeight: 600, color: '#1f2937', marginTop: 5, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflowWrap: 'anywhere' }}
      >
        {exp.cliente_nombre || 'Sin cliente'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, color: '#8a9099', fontSize: 11.5 }}>
        <User size={12} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exp.responsable_nombre || 'Sin responsable'}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 9 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
          color: sem.color, background: sem.bg, border: `1px solid ${sem.borde}`, padding: '2px 8px', borderRadius: 999,
        }}>
          <Clock size={11} /> {textoDias(exp.dias_en_paso)}
        </span>
        <span title={dinero.titulo} style={{ fontSize: 12.5, fontWeight: 700, color: dinero.color }}>
          {dinero.texto}
        </span>
      </div>
      {exp.semaforo === 'rojo' && onAvisar && <BotonAvisar exp={exp} onAvisar={onAvisar} full />}
    </div>
  );
}

// ---------- Vista Kanban ----------
// Margen que se deja debajo del carril del Kanban para que la barra de scroll horizontal
// quede holgada dentro de la ventana (y no pegada al borde inferior).
const MARGEN_INFERIOR_KANBAN = 18;
// Separaciones del interior de una columna (deben coincidir con los estilos de abajo):
// hueco entre tarjetas y hueco entre la cabecera y la primera tarjeta.
const GAP_TARJETAS = 8;
const GAP_CABECERA = 8;
// Tope de alto del tablero. En pantallas muy altas (PC VERTICAL 1080x1920) un carril de ~1800 px
// obliga a barrer la pantalla con la vista y deja las columnas cortas con un hueco enorme debajo.
// Con el tope, el tablero se queda en una franja comoda y cada columna scrollea por dentro.
const TOPE_ALTO_KANBAN = 900;
// MOVIL-2 (A4): en celular el tablero NO se mide restando su posicion en la pagina. El Kanban vive
// DEBAJO del resumen y de los filtros, asi que al cargar su borde superior cae fuera de la ventana
// y "el alto disponible" salia negativo: la columna se quedaba en el minimo de 220 px, es decir la
// ventanita corta que Ricardo reporto. En celular se le da una FRANJA FIJA proporcional a la
// pantalla (con el respaldo de --beep-vh, igual que los modales), para que la columna siempre
// llegue de lado a lado del alto util y quepan ~5 tarjetas compactas.
const ALTO_KANBAN_MOVIL = 'calc(var(--beep-vh) * 72)';

export function VistaKanban({ columnas, expedientes, claveColumna, onAbrir, onAvisar, esCelular, onIrTabla }) {
  const grupos = useMemo(() => {
    const map = new Map();
    columnas.forEach((c) => map.set(c.key, []));
    // Un paso fuera del catalogo de columnas (legacy) simplemente no se agrupa.
    expedientes.forEach((e) => {
      const k = claveColumna(e);
      if (map.has(k)) map.get(k).push(e);
    });
    return map;
  }, [columnas, expedientes, claveColumna]);

  // ---------- KANBAN-SCROLL: carril acotado + afordancias ----------
  const railRef = useRef(null);
  const [alto, setAlto] = useState(null);
  const [bordes, setBordes] = useState({ izq: false, der: false });
  const [arrastrando, setArrastrando] = useState(false);

  // Alto del carril = el MENOR entre lo que cabe en la ventana y lo que el contenido necesita.
  // Hallazgo de Dona Frida (2026-09-10): con solo el alto disponible, en PC VERTICAL (1080x1920)
  // el carril media ~1793 px y las columnas con pocas tarjetas quedaban con ~1000 px en blanco
  // debajo (las columnas son flex y se estiran a la altura del carril). Ahora el carril se ajusta
  // al contenido cuando cabe, y solo se acota cuando el contenido es mas alto que la pantalla.
  const medirAlto = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    // En celular manda la franja fija (ver ALTO_KANBAN_MOVIL): no se mide contra la ventana.
    if (esCelular) { setAlto(null); return; }
    const top = el.getBoundingClientRect().top;
    const disponible = Math.round((window.visualViewport?.height || window.innerHeight) - top - MARGEN_INFERIOR_KANBAN);
    // Alto NATURAL de la columna mas larga: se suma la altura de sus tarjetas, NO el scrollHeight
    // del contenedor (que ya viene inflado por el alto que le pusimos y nunca dejaria encoger).
    let contenido = 0;
    for (const col of el.children) {
      const cab = col.firstElementChild;                    // cabecera de la columna
      const cuerpo = col.querySelector('.beep-kanban-col');  // lista de tarjetas
      if (!cuerpo) continue;
      const hijos = [...cuerpo.children];
      const tarjetas = hijos.reduce((a, t) => a + t.offsetHeight, 0) + Math.max(0, hijos.length - 1) * GAP_TARJETAS;
      contenido = Math.max(contenido, (cab ? cab.offsetHeight + GAP_CABECERA : 0) + tarjetas);
    }
    const objetivo = contenido > 0 ? Math.min(disponible, contenido, TOPE_ALTO_KANBAN) : Math.min(disponible, TOPE_ALTO_KANBAN);
    setAlto(Math.max(220, objetivo));
  }, [esCelular]);

  // ¿Hay contenido oculto a izquierda/derecha? Decide las sombras de borde.
  const medirBordes = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setBordes({ izq: el.scrollLeft > 2, der: el.scrollLeft < max - 2 });
  }, []);

  useLayoutEffect(() => { medirAlto(); medirBordes(); }, [medirAlto, medirBordes, columnas, expedientes]);
  useEffect(() => {
    const alRedimensionar = () => { medirAlto(); medirBordes(); };
    window.addEventListener('resize', alRedimensionar);
    window.visualViewport?.addEventListener('resize', alRedimensionar);
    return () => {
      window.removeEventListener('resize', alRedimensionar);
      window.visualViewport?.removeEventListener('resize', alRedimensionar);
    };
  }, [medirAlto, medirBordes]);

  // ---- MOVIL-2 (2026-09-14): carril tipo Trello en celular ----
  // Antes se veia "una columna y pico" y cada columna tenia una ventanita de scroll muy corta que
  // cortaba las tarjetas por arriba y por abajo, con media pantalla vacia debajo. Ahora: UNA columna
  // por pantalla (88% de ancho, con un asomo de la siguiente para que se entienda que hay mas),
  // salto con el dedo por scroll-snap, y la columna ocupa TODO el alto disponible.
  const [colActiva, setColActiva] = useState(0);
  const alScrollCarril = useCallback(() => {
    medirBordes();
    const el = railRef.current;
    if (!el || !esCelular) return;
    const hijos = [...el.children];
    if (!hijos.length) return;
    // la columna activa es la que tiene su borde izquierdo mas cerca del borde del carril
    let mejor = 0, mejorDist = Infinity;
    const base = el.getBoundingClientRect().left;
    hijos.forEach((h, i) => {
      const d = Math.abs(h.getBoundingClientRect().left - base);
      if (d < mejorDist) { mejorDist = d; mejor = i; }
    });
    setColActiva(prev => (prev === mejor ? prev : mejor));
  }, [medirBordes, esCelular]);

  // A8 — cuantas tarjetas quedan POR DEBAJO del borde visible de cada columna. Se cuenta con la
  // geometria real (que tarjeta cae bajo el borde inferior de su lista), no con un numero fijo:
  // asi da igual el alto del telefono o que las tarjetas midan distinto.
  const columnasRef = useRef(new Map());
  const [restantes, setRestantes] = useState(new Map());
  const contarRestantes = useCallback((clave, el) => {
    if (!el) return;
    const fondo = el.getBoundingClientRect().bottom;
    let n = 0;
    for (const t of el.children) if (t.getBoundingClientRect().bottom > fondo + 4) n++;
    setRestantes(prev => (prev.get(clave) === n ? prev : new Map(prev).set(clave, n)));
  }, []);
  const registrarColumna = useCallback((clave, el) => {
    if (el) { columnasRef.current.set(clave, el); contarRestantes(clave, el); }
    else columnasRef.current.delete(clave);
  }, [contarRestantes]);
  const alScrollColumna = useCallback((clave, el) => contarRestantes(clave, el), [contarRestantes]);
  // Al cambiar los datos o el alto del carril hay que volver a contar: las tarjetas se movieron.
  useEffect(() => {
    if (!esCelular) return;
    const t = setTimeout(() => {
      columnasRef.current.forEach((el, clave) => contarRestantes(clave, el));
    }, 60);
    return () => clearTimeout(t);
  }, [esCelular, expedientes, columnas, alto, contarRestantes]);

  // TOTAL EN DINERO por columna. NO se recalcula nada: se SUMA el `monto` que ya resolvio el
  // backend (cotizacion autorizada, si no la ultima capturada). Los expedientes sin cotizar no
  // suman: no hay cifra que sumar todavia.
  const totalDeColumna = useCallback((items) => {
    let n = 0;
    for (const e of items) if (e && e.monto != null) n += Number(e.monto) || 0;
    return n;
  }, []);
  // EXPEDIENTES-RESUMEN (2026-09-17): desde este sprint el servidor no manda los montos de los
  // expedientes ajenos a quien no sea direccion. Eso obliga a dos cosas en la cabecera de columna:
  //   1. decir que el total es SOLO de lo suyo (si no, se leeria como el total de la columna y
  //      seria una cifra falsa);
  //   2. no contar esos huecos como "sin cotizar" — no es que no tengan cotizacion, es que su
  //      importe no le toca verlo.
  const ocultosEnColumna = useCallback((items) => items.filter(e => e && e.monto_oculto).length, []);
  const sinCotizarEnColumna = useCallback(
    (items) => items.filter(e => e && !e.monto_oculto && e.monto == null).length, []);

  // Rueda con Shift = avanzar de lado (atajo estandar de tableros).
  const onWheelRail = useCallback((ev) => {
    const el = railRef.current;
    if (!el || !ev.shiftKey) return;
    el.scrollLeft += ev.deltaY;
  }, []);

  // Arrastre con el raton SOBRE EL FONDO del carril. Nunca sobre una tarjeta ni sobre un boton:
  // asi no se pisa el clic para abrir el expediente (ni un futuro drag-and-drop de tarjetas).
  const onMouseDownRail = useCallback((ev) => {
    const el = railRef.current;
    if (!el || ev.button !== 0) return;
    if (ev.target.closest('.beep-card-exp, button, a, input, select, textarea')) return;
    const xInicial = ev.clientX;
    const scrollInicial = el.scrollLeft;
    let movido = false;
    const mover = (e2) => {
      const dx = e2.clientX - xInicial;
      if (!movido && Math.abs(dx) > 3) { movido = true; setArrastrando(true); }
      if (movido) { el.scrollLeft = scrollInicial - dx; medirBordes(); }
    };
    const soltar = () => {
      setArrastrando(false);
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', soltar);
    };
    window.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', soltar);
  }, [medirBordes]);

  const sombraLado = (lado) => ({
    position: 'absolute', top: 0, bottom: 14, width: 34, pointerEvents: 'none', zIndex: 2,
    [lado === 'izq' ? 'left' : 'right']: 0,
    background: `linear-gradient(to ${lado === 'izq' ? 'right' : 'left'}, rgba(31,41,55,0.13), rgba(31,41,55,0))`,
  });

  return (
    <div>
      {/* A2 — INDICADOR DE POSICION: puntitos (el activo alargado) + "Nombre · N de M".
          Sustituye al aviso de "desliza de lado": ahora se VE donde estas, no hay que explicarlo. */}
      {esCelular && columnas.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '0 2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {columnas.map((c, i) => (
              <span key={c.key} style={{
                width: i === colActiva ? 16 : 6, height: 6, borderRadius: 999,
                background: i === colActiva ? '#2f5f9e' : '#d1d5db',
                transition: 'width .18s ease, background .18s ease',
              }} />
            ))}
          </div>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#4e5357', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(columnas[colActiva] || {}).label} · {colActiva + 1} de {columnas.length}
          </span>
          <button
            type="button" onClick={onIrTabla}
            style={{
              marginLeft: 'auto', flexShrink: 0, minHeight: 44, padding: '0 10px',
              background: 'transparent', border: 'none', color: '#2f5f9e',
              fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >Tabla <ChevronRight size={14} /></button>
        </div>
      )}
      {/* KANBAN-SCROLL (2026-09-10): el carril es un SCROLLPORT ACOTADO al alto disponible.
          Antes crecia con las tarjetas, asi que su barra de scroll horizontal quedaba cientos de
          pixeles por debajo del viewport: existia, pero era inalcanzable, y la ultima columna se
          veia cortada sin forma de avanzar. Ahora el alto se mide contra el fondo de la ventana,
          cada columna hace su propio scroll vertical y la barra horizontal queda SIEMPRE a la vista.
          Afordancias: barra visible, sombras en los bordes con contenido por delante/detras,
          arrastre con el raton sobre el fondo y rueda con Shift. */}
      <div style={{ position: 'relative' }}>
        <div
          ref={railRef}
          className="beep-kanban-scroll beep-kanban-rail"
          onScroll={alScrollCarril}
          onWheel={onWheelRail}
          onMouseDown={onMouseDownRail}
          style={{
            display: 'flex', gap: esCelular ? 8 : 12, overflowX: 'auto', overflowY: 'hidden',
            height: esCelular ? ALTO_KANBAN_MOVIL : (alto ? `${alto}px` : undefined), minHeight: 220,
            paddingBottom: 4, WebkitOverflowScrolling: 'touch', overscrollBehaviorX: 'contain',
            cursor: arrastrando ? 'grabbing' : undefined,
            // A1: salto con el dedo, una columna por pantalla.
            scrollSnapType: esCelular ? 'x mandatory' : undefined,
          }}
        >
          {columnas.map((col) => {
            const items = grupos.get(col.key) || [];
            return (
              <div key={col.key} style={{
                flex: '0 0 auto',
                // A1: 88% con asomo de la siguiente (~12%) para que se vea que hay mas columnas.
                width: esCelular ? '88%' : 268,
                display: 'flex', flexDirection: 'column', minHeight: 0,
                scrollSnapAlign: esCelular ? 'start' : undefined,
              }}>
                {/* A3 — ENCABEZADO FIJO: nombre + conteo en pastilla + TOTAL EN DINERO de la columna.
                    El total SUMA el `monto` que ya resolvio el backend; aqui no se calcula dinero. */}
                <div style={{
                  padding: '8px 11px', background: '#ffffff', borderRadius: 10, border: '1px solid #e6e8eb', marginBottom: 8,
                  flexShrink: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: '#4e5357', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.label}</span>
                    <span style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: '#8a9099', background: '#f3f4f4', borderRadius: 999, padding: '1px 8px', minWidth: 22, textAlign: 'center' }}>{items.length}</span>
                  </div>
                  {items.length > 0 && (() => {
                    const total = totalDeColumna(items);
                    const ocultos = ocultosEnColumna(items);
                    const sinCot = sinCotizarEnColumna(items);
                    // Con montos ajenos ocultos y nada propio que sumar, un "$0" MENTIRIA: no es que
                    // la columna no tenga dinero, es que no le toca verlo. Se calla la cifra.
                    const callarElTotal = ocultos > 0 && total === 0;
                    if (callarElTotal && sinCot === 0) return null;
                    return (
                      <div style={{ marginTop: 2, fontSize: 11.5, fontWeight: 700, color: '#2f5f9e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {!callarElTotal && formatMoneda(total, true)}
                        {!callarElTotal && ocultos > 0 && (
                          <span
                            title="Solo se suma el dinero de los expedientes de los que eres responsable"
                            style={{ marginLeft: 5, fontWeight: 600, color: '#8a9099' }}
                          >{'·'} de los tuyos</span>
                        )}
                        {sinCot > 0 && (
                          <span style={{ marginLeft: callarElTotal ? 0 : 6, fontWeight: 600, color: '#c2c7ce' }}>
                            {/* Con el total callado, este numero se quedaba solo y se leia como si
                                fuera de TODA la columna. Se dice de quien es. */}
                            {callarElTotal ? '' : '· '}{sinCot} sin cotizar{callarElTotal && ocultos > 0 ? ' de los tuyos' : ''}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {/* Las tarjetas scrollean DENTRO de la columna: asi el carril no crece de alto.
                    A8: el aviso de "quedan mas" va PEGADO AL BORDE de la columna (no al final de la
                    lista): asi se lee mientras todavia hay tarjetas debajo, y desaparece al llegar
                    al fondo. Un texto "sigue bajando" colocado al final diria lo contrario de lo
                    que pasa, porque para leerlo ya bajaste todo. */}
                <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
                  <div
                    className="beep-kanban-col"
                    onScroll={esCelular ? ((ev) => alScrollColumna(col.key, ev.currentTarget)) : undefined}
                    ref={esCelular ? ((el) => registrarColumna(col.key, el)) : undefined}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 40, overflowY: 'auto', flex: 1, paddingRight: 2 }}
                  >
                    {items.length === 0
                      ? <div style={{ fontSize: 11.5, color: '#c2c7ce', textAlign: 'center', padding: '14px 0', border: '1px dashed #e6e8eb', borderRadius: 10 }}>Vacio</div>
                      : items.map((e, i) => (
                          <TarjetaExpediente key={e.id} exp={e} onAbrir={onAbrir} onAvisar={onAvisar} idx={i} compacta={esCelular} />
                        ))}
                  </div>
                  {esCelular && (restantes.get(col.key) || 0) > 0 && (
                    <div style={{
                      position: 'absolute', left: 0, right: 2, bottom: 0, pointerEvents: 'none',
                      padding: '14px 0 5px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#6b7280',
                      background: 'linear-gradient(to bottom, rgba(243,244,244,0), #f3f4f4 62%)',
                      borderRadius: '0 0 10px 10px',
                    }}>
                      {restantes.get(col.key)} mas · sigue bajando
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* Sombras de borde: avisan que hay mas columnas hacia ese lado. No capturan el raton. */}
        {bordes.izq && <div aria-hidden style={sombraLado('izq')} />}
        {bordes.der && <div aria-hidden style={sombraLado('der')} />}
      </div>
    </div>
  );
}

// ---------- Vista Tabla ----------
const COLUMNAS_TABLA = [
  { key: 'folio', label: 'Folio', orden: true },
  { key: 'cliente', label: 'Cliente', orden: true },
  { key: 'tipo', label: 'Tipo', orden: true },
  { key: 'paso', label: 'Paso actual', orden: true },
  { key: 'responsable', label: 'Responsable', orden: true },
  { key: 'dias', label: 'Dias', orden: true },
  { key: 'monto', label: 'Monto', orden: true },
  { key: 'actualizado', label: 'Actualizado', orden: true },
  { key: 'accion', label: '', orden: false },
];

function valorOrden(exp, campo) {
  switch (campo) {
    case 'folio': return exp.folio || '';
    case 'cliente': return (exp.cliente_nombre || '').toLowerCase();
    case 'tipo': return exp.workflow_codigo || '';
    case 'paso': return exp.paso_orden ?? 999;
    case 'responsable': return (exp.responsable_nombre || '').toLowerCase();
    case 'dias': return Number(exp.dias_en_paso) || 0;
    case 'monto': return exp.monto == null ? -1 : Number(exp.monto);
    case 'actualizado': return exp.actualizado_at ? new Date(exp.actualizado_at).getTime() : 0;
    default: return 0;
  }
}

function VistaTabla({ expedientes, orden, setOrden, onAbrir, onAvisar, compacto }) {
  const ordenados = useMemo(() => {
    const arr = [...expedientes];
    arr.sort((a, b) => {
      const va = valorOrden(a, orden.campo);
      const vb = valorOrden(b, orden.campo);
      if (va < vb) return orden.dir === 'asc' ? -1 : 1;
      if (va > vb) return orden.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [expedientes, orden]);

  const toggleOrden = (campo) => {
    setOrden((o) => o.campo === campo
      ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: campo === 'dias' || campo === 'monto' ? 'desc' : 'asc' });
  };

  // En pantallas chicas: tarjetas en vez de tabla (diseno-beep: tablas -> tarjetas en movil).
  if (compacto) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {ordenados.map((e, i) => <TarjetaTabla key={e.id} exp={e} onAbrir={onAbrir} onAvisar={onAvisar} idx={i} />)}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e6e8eb', borderRadius: 14 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FF }}>
        <thead>
          <tr style={{ background: '#f7f9fb', borderBottom: '1px solid #e6e8eb' }}>
            {COLUMNAS_TABLA.map((c) => {
              const activo = orden.campo === c.key;
              return (
                <th
                  key={c.key}
                  onClick={c.orden ? () => toggleOrden(c.key) : undefined}
                  style={{
                    textAlign: c.key === 'monto' || c.key === 'dias' ? 'right' : 'left',
                    padding: '11px 14px', fontSize: 11.5, fontWeight: 700, color: activo ? '#2f5f9e' : '#8a9099',
                    textTransform: 'uppercase', letterSpacing: .3, cursor: c.orden ? 'pointer' : 'default', whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: c.key === 'monto' || c.key === 'dias' ? 'flex-end' : 'flex-start' }}>
                    {c.label}
                    {c.orden && <ArrowUpDown size={12} style={{ opacity: activo ? 1 : .35 }} />}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordenados.map((e, i) => {
            const sem = SEMAFORO[e.semaforo] || SEMAFORO.verde;
            const tipo = TIPO_VISUAL[e.workflow_codigo] || { label: e.workflow_nombre || '—', color: '#4e5357', bg: '#f3f4f4' };
            return (
              <tr key={e.id} className="beep-fila-exp" onClick={() => onAbrir(e.id)}
                style={{ borderBottom: '1px solid #f0f2f4', animation: `beepFadeUp .2s ease-out ${Math.min(i, 12) * 20}ms both` }}>
                <td style={{ padding: '11px 14px', fontSize: 12.5, fontWeight: 700, color: '#2f5f9e', whiteSpace: 'nowrap' }}>{e.folio || `#${e.id}`}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, color: '#1f2937', maxWidth: 240 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.cliente_nombre}</div>
                  {e.cliente_jerarquia_corta && e.cliente_jerarquia_corta !== e.cliente_nombre && (
                    <div style={{ fontSize: 11, color: '#a4abb4', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.cliente_jerarquia_corta}</div>
                  )}
                </td>
                <td style={{ padding: '11px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: tipo.color, background: tipo.bg, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>{tipo.label}</span>
                </td>
                <td style={{ padding: '11px 14px', fontSize: 12.5, color: '#4e5357', whiteSpace: 'nowrap' }}>{e.paso_nombre || e.estado}</td>
                <td style={{ padding: '11px 14px', fontSize: 12.5, color: '#4e5357', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.responsable_nombre || '—'}</td>
                <td style={{ padding: '11px 14px', textAlign: 'right' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: sem.color, background: sem.bg, border: `1px solid ${sem.borde}`, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: sem.color }} /> {textoDias(e.dias_en_paso)}
                  </span>
                </td>
                <td style={{ padding: '11px 14px', textAlign: 'right', fontSize: 13, fontWeight: 700, color: e.monto != null ? '#1f2937' : '#c2c7ce', whiteSpace: 'nowrap' }}>{formatMoneda(e.monto)}<MarcaSinAutorizar exp={e} /></td>
                <td style={{ padding: '11px 14px', fontSize: 12, color: '#8a9099', whiteSpace: 'nowrap' }}>{formatFechaCorta(e.actualizado_at)}</td>
                <td style={{ padding: '11px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {e.semaforo === 'rojo' && onAvisar && <BotonAvisar exp={e} onAvisar={onAvisar} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TarjetaTabla({ exp, onAbrir, onAvisar, idx }) {
  const sem = SEMAFORO[exp.semaforo] || SEMAFORO.verde;
  const tipo = TIPO_VISUAL[exp.workflow_codigo] || { label: exp.workflow_nombre || '—', color: '#4e5357', bg: '#f3f4f4' };
  return (
    <div className="beep-card-exp" onClick={() => onAbrir(exp.id)}
      style={{ background: '#ffffff', border: '1px solid #e6e8eb', borderLeft: `3px solid ${sem.color}`, borderRadius: 12, padding: '12px 14px', cursor: 'pointer', animation: `beepFadeUp .22s ease-out ${Math.min(idx, 12) * 22}ms both` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#2f5f9e' }}>{exp.folio || `#${exp.id}`}</span>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: tipo.color, background: tipo.bg, padding: '2px 7px', borderRadius: 6 }}>{tipo.label}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginTop: 5 }}>{exp.cliente_nombre}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: '#8a9099' }}>
        <span>{exp.paso_nombre || exp.estado}</span>
        <span>·</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><User size={12} />{exp.responsable_nombre || 'Sin responsable'}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 9 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: sem.color, background: sem.bg, border: `1px solid ${sem.borde}`, padding: '2px 8px', borderRadius: 999 }}>
          <Clock size={11} /> {textoDias(exp.dias_en_paso)}
        </span>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: exp.monto != null ? '#1f2937' : '#c2c7ce' }}>{formatMoneda(exp.monto)}<MarcaSinAutorizar exp={exp} /></span>
      </div>
      {exp.semaforo === 'rojo' && onAvisar && <BotonAvisar exp={exp} onAvisar={onAvisar} full />}
    </div>
  );
}

// ---------- Estados de la vista ----------
function EstadoCargando({ vista, esCelular }) {
  if (vista === 'kanban') {
    return (
      <div style={{ display: 'flex', gap: 12, overflow: 'hidden' }}>
        {[0, 1, 2, 3].map((c) => (
          <div key={c} style={{ flex: '0 0 auto', width: esCelular ? 250 : 268 }}>
            <div className="beep-skel" style={{ height: 36, marginBottom: 8 }} />
            {[0, 1, 2].map((k) => <div key={k} className="beep-skel" style={{ height: 96, marginBottom: 8 }} />)}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', border: '1px solid #e6e8eb', borderRadius: 14, padding: 14 }}>
      {[0, 1, 2, 3, 4, 5].map((k) => <div key={k} className="beep-skel" style={{ height: 40, marginBottom: 8 }} />)}
    </div>
  );
}

function EstadoError({ tipo, onReintentar }) {
  const permiso = tipo === 'permiso';
  return (
    <div style={{ textAlign: 'center', padding: '48px 20px', color: '#8a9099' }}>
      <AlertTriangle size={40} style={{ color: permiso ? '#d97706' : '#dc2626', marginBottom: 12 }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
        {permiso ? 'Este panel es solo para direccion' : 'No pudimos cargar la torre de control'}
      </div>
      <div style={{ fontSize: 13, marginTop: 4 }}>
        {permiso ? 'Tu usuario no tiene acceso a esta vista.' : 'Revisa tu conexion e intenta de nuevo.'}
      </div>
      {!permiso && (
        <button onClick={onReintentar} style={{ marginTop: 16, padding: '10px 18px', borderRadius: 10, border: 'none', background: '#2f5f9e', color: '#fff', fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Reintentar
        </button>
      )}
    </div>
  );
}

function EstadoVacio({ hayFiltros, onLimpiar }) {
  return (
    <div style={{ textAlign: 'center', padding: '52px 20px', color: '#8a9099' }}>
      <Inbox size={44} style={{ color: '#c2c7ce', marginBottom: 12 }} />
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
        {hayFiltros ? 'Ningun expediente coincide con los filtros' : 'No hay expedientes activos'}
      </div>
      <div style={{ fontSize: 13, marginTop: 4 }}>
        {hayFiltros ? 'Prueba a quitar algun filtro para ver mas.' : 'Cuando se creen expedientes apareceran aqui.'}
      </div>
      {hayFiltros && (
        <button onClick={onLimpiar} style={{ marginTop: 16, padding: '10px 18px', borderRadius: 10, border: '1px solid #e6e8eb', background: '#fff', color: '#2f5f9e', fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Limpiar filtros
        </button>
      )}
    </div>
  );
}
